import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthService from '../../API/AuthService';
import useUserStore from '../../store/userStore';
import cl from './Register.module.css';
import { IconArrowRight, IconArrowLeft, IconCheck } from '../../components/icons';
import usePageMeta from '../../hooks/usePageMeta';
import YandexLoginButton from '../../components/OAuth/YandexLoginButton';
import GoogleMigrationModal from '../../components/OAuth/GoogleMigrationModal';
import TripleCaptcha from '../../components/UI/TripleCaptcha/TripleCaptcha';

// Двухшаговая регистрация центра.
// Шаг 1 — контактные данные (ФИО, e-mail, пароль, телефон).
// Шаг 2 — данные центра + согласия (политика, оферта, представитель центра).
//
// Защита от ботов:
//   - Honeypot-поле "company" (скрытое CSS-ом). Боты заполнят всё подряд,
//     люди не увидят. Если оно непустое — игнорируем форму.
//   - Минимальное время заполнения: если форма отправлена за <2с с момента
//     открытия, это бот. Просто отвергаем.
export default function Register() {
    usePageMeta({
        title: 'Регистрация · СтартКласс',
        description: 'Создайте кабинет руководителя детского центра и получите бесплатный демо-доступ к платформе СтартКласс.',
    });
    const navigate = useNavigate();
    const refresh = useUserStore(s => s.refresh);

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [showGoogleHelp, setShowGoogleHelp] = useState(false);
    const [err, setErr] = useState('');

    // Шаг 1
    const [firstName, setFirstName] = useState('');
    const [email,     setEmail]     = useState('');
    const [password,  setPassword]  = useState('');
    const [phone,     setPhone]     = useState('');

    // Шаг 2
    const [centreName, setCentreName] = useState('');
    const [city,       setCity]       = useState('');
    const [type,       setType]       = useState('IP'); // 'IP' | 'OOO'
    const [inn,        setInn]        = useState('');

    // Согласия (шаг 2)
    const [consentPrivacy, setConsentPrivacy] = useState(false);
    const [consentOferta,  setConsentOferta]  = useState(false);

    // Honeypot — скрытое поле для ботов
    const [hpCompany, setHpCompany] = useState('');
    // Время открытия формы — для проверки скорости заполнения
    const [openedAt] = useState(() => Date.now());

    // 2026-05-29: adaptive-капча. Бэк включает её при velocity-burst (>2 рег/30с).
    // Проверяем при загрузке формы и заново при ошибке captcha_required от сервера.
    const [captchaRequired, setCaptchaRequired] = useState(false);
    const captchaRef = useRef(null);
    useEffect(() => {
        AuthService.isCaptchaRequired().then(setCaptchaRequired);
    }, []);

    function validateStep1() {
        if (!firstName.trim())  return 'Укажите ваше имя';
        if (!email.trim())      return 'Укажите e-mail';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Некорректный e-mail';
        if (password.length < 6) return 'Пароль должен быть не короче 6 символов';
        return null;
    }
    function validateStep2() {
        if (!centreName.trim()) return 'Укажите название центра';
        if (inn && !/^\d{10}$|^\d{12}$/.test(inn.trim())) {
            return 'ИНН должен содержать 10 или 12 цифр';
        }
        if (!consentPrivacy) return 'Подтвердите согласие на обработку персональных данных';
        if (!consentOferta)  return 'Подтвердите согласие с публичной офертой';
        return null;
    }

    function next(e) {
        e.preventDefault();
        const v = validateStep1();
        if (v) { setErr(v); return; }
        setErr('');
        setStep(2);
    }

    async function submit(e) {
        e.preventDefault();

        // Anti-bot: заполнили honeypot — молча отвергаем (бот не должен
        // узнать что мы его поймали, чтобы он не перепробовал)
        if (hpCompany) {
            setErr('Не удалось зарегистрироваться');
            return;
        }
        // Anti-bot: заполнили за <2 секунды — это бот
        if (Date.now() - openedAt < 2000) {
            setErr('Слишком быстро. Попробуйте ещё раз через секунду.');
            return;
        }

        const v = validateStep2();
        if (v) { setErr(v); return; }

        // Если адаптивная капча активна — собираем ответы и шлём вместе.
        // Бэк (signup_guard.py) проверит и при ошибке вернёт 403 captcha_required.
        let captchaPayload = null;
        if (captchaRequired) {
            if (!captchaRef.current || !captchaRef.current.isReady()) {
                setErr('Заполните проверку: введите 2 кода и нарисуйте кота');
                return;
            }
            captchaPayload = captchaRef.current.collect();
        }

        setErr('');
        setLoading(true);

        const res = await AuthService.register({
            firstName: firstName.trim(),
            email: email.trim().toLowerCase(),
            password,
            centreName: centreName.trim(),
            city: city.trim() || undefined,
            phone: phone.trim() || undefined,
            inn: inn.trim() || undefined,
            consent: {
                privacy: true,
                oferta: true,
                accepted_at: Math.floor(Date.now() / 1000),
            },
            captcha: captchaPayload,
        });

        if (res.ok) {
            await refresh();
            navigate('/cabinet');
        } else if (res.captchaRequired) {
            // Бэк включил капчу прямо сейчас (velocity-burst после старта формы).
            setCaptchaRequired(true);
            if (captchaRef.current) captchaRef.current.refresh();
            setErr('Заполните проверку — обновили картинки');
        } else {
            setErr(res.message || 'Не удалось зарегистрироваться');
        }
        setLoading(false);
    }

    return (
        <main className={cl.page}>
            <form className={cl.card} onSubmit={step === 1 ? next : submit} autoComplete="on">
                <div className={cl.head}>
                    <span className={cl.eyebrow}>Регистрация центра</span>
                    <h1 className={cl.title}>
                        {step === 1 ? 'Контактные данные' : 'Данные центра'}
                    </h1>
                    <p className={cl.lead}>
                        {step === 1
                            ? 'Чтобы вы могли войти в кабинет и пользоваться демо.'
                            : 'Заполните, как ваш центр представлен в документах.'
                        }
                    </p>
                </div>

                <div className={cl.steps}>
                    <div className={`${cl.step} ${step >= 1 ? (step > 1 ? cl.done : cl.active) : ''}`}/>
                    <div className={`${cl.step} ${step >= 2 ? cl.active : ''}`}/>
                </div>
                <div className={cl.stepLabel}>Шаг {step} из 2</div>

                {err && <div className={cl.err}>{err}</div>}

                {/* === Honeypot: скрытое поле, заполняется только ботами === */}
                <div className={cl.hpField} aria-hidden="true">
                    <label htmlFor="company">Компания (не заполнять)</label>
                    <input
                        type="text" id="company" name="company"
                        tabIndex={-1} autoComplete="off"
                        value={hpCompany}
                        onChange={e => setHpCompany(e.target.value)}
                    />
                </div>

                {step === 1 && (
                    <>
                        {/* OAuth-вход на шаге 1. Если у юзера уже есть аккаунт
                            на Майнкрафтии — нажмёт Яндекс/Google и сразу попадёт
                            в кабинет. Регистрация центра — отдельным шагом потом. */}
                        <div style={{ marginBottom: 14 }}>
                            <YandexLoginButton disabled={loading} />
                            {/* Кнопка Google убрана: вход через иностранные сервисы
                                на российских сайтах запрещён (149-ФЗ). Плашка ведёт
                                на инструкцию восстановления доступа по паролю. */}
                            <button
                                type="button"
                                onClick={() => setShowGoogleHelp(true)}
                                style={{
                                    width: '100%',
                                    marginTop: 10,
                                    padding: '11px 14px',
                                    borderRadius: 12,
                                    border: '1px solid #e2e8f0',
                                    background: '#f8fafc',
                                    color: '#475569',
                                    fontSize: 13.5,
                                    fontWeight: 600,
                                    fontFamily: 'inherit',
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                }}
                            >
                                Раньше входили через Google?
                            </button>
                            <div style={{
                                textAlign: 'center',
                                margin: '14px 0 4px',
                                color: '#94a3b8',
                                fontSize: 13,
                                position: 'relative',
                            }}>
                                <span style={{ background: '#fff', padding: '0 12px', position: 'relative', zIndex: 1 }}>
                                    или зарегистрироваться по e-mail
                                </span>
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: 0, right: 0,
                                    height: 1,
                                    background: '#e6e7eb',
                                    zIndex: 0,
                                }} />
                            </div>
                        </div>

                        <div className={cl.field}>
                            <label htmlFor="firstName">Ваше имя</label>
                            <input id="firstName" type="text" required value={firstName}
                                onChange={e => setFirstName(e.target.value)}
                                placeholder="Иван" autoComplete="given-name"/>
                        </div>
                        <div className={cl.field}>
                            <label htmlFor="email">E-mail</label>
                            <input id="email" type="email" required value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="you@example.com" autoComplete="email"/>
                            <span className={cl.fieldHint}>
                                Логин и адрес для подтверждения регистрации
                            </span>
                        </div>
                        <div className={cl.field}>
                            <label htmlFor="password">Пароль</label>
                            <input id="password" type="password" required minLength={6} value={password}
                                onChange={e => setPassword(e.target.value)}
                                placeholder="минимум 6 символов" autoComplete="new-password"/>
                        </div>
                        <div className={cl.field}>
                            <label htmlFor="phone">Телефон (необязательно)</label>
                            <input id="phone" type="tel" value={phone}
                                onChange={e => setPhone(e.target.value)}
                                placeholder="+7 ___ ___-__-__" autoComplete="tel"/>
                        </div>

                        <div className={cl.actions}>
                            <button type="submit" className={cl.btnPrimary}>
                                Дальше <IconArrowRight width={16} height={16}/>
                            </button>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <div className={cl.field}>
                            <label htmlFor="centreName">Название центра</label>
                            <input id="centreName" type="text" required value={centreName}
                                onChange={e => setCentreName(e.target.value)}
                                placeholder='Например, "КодШкола"'
                                autoComplete="organization"/>
                        </div>
                        <div className={cl.field}>
                            <label htmlFor="city">Город (необязательно)</label>
                            <input id="city" type="text" value={city}
                                onChange={e => setCity(e.target.value)}
                                placeholder="Краснодар"
                                autoComplete="address-level2"/>
                        </div>
                        <div className={cl.field}>
                            <label>Юридическая форма</label>
                            <div className={cl.toggleType}>
                                <button type="button"
                                    className={type === 'IP' ? cl.activeType : ''}
                                    onClick={() => setType('IP')}>
                                    ИП
                                </button>
                                <button type="button"
                                    className={type === 'OOO' ? cl.activeType : ''}
                                    onClick={() => setType('OOO')}>
                                    ООО
                                </button>
                            </div>
                        </div>
                        <div className={cl.field}>
                            <label htmlFor="inn">ИНН (необязательно сейчас)</label>
                            <input id="inn" type="text" inputMode="numeric"
                                value={inn} onChange={e => setInn(e.target.value)}
                                placeholder={type === 'IP' ? '12 цифр' : '10 цифр'}/>
                            <span className={cl.fieldHint}>
                                Понадобится для счёта при оплате. Можно заполнить позже в кабинете.
                            </span>
                        </div>

                        {/* === Согласия === */}
                        <div className={cl.consents}>
                            <ConsentCheckbox
                                checked={consentPrivacy}
                                onChange={setConsentPrivacy}
                                id="consent-privacy"
                            >
                                Я даю согласие на обработку моих <Link to="/privacy" target="_blank" rel="noopener noreferrer">персональных данных</Link> на условиях, указанных в политике конфиденциальности.
                            </ConsentCheckbox>
                            <ConsentCheckbox
                                checked={consentOferta}
                                onChange={setConsentOferta}
                                id="consent-oferta"
                            >
                                Я принимаю условия <Link to="/oferta" target="_blank" rel="noopener noreferrer">публичной оферты</Link> на оказание услуг.
                            </ConsentCheckbox>
                        </div>

                        <div className={cl.note}>
                            <b>После регистрации</b> вам откроется демо-доступ — 50 учеников и
                            5 курсов бесплатно, чтобы спокойно посмотреть платформу.
                        </div>

                        {/* Adaptive-капча: показываем только когда сервер просит. */}
                        {captchaRequired && (
                            <TripleCaptcha ref={captchaRef} disabled={loading} />
                        )}

                        <div className={cl.actions}>
                            <button type="button" className={cl.btnGhost}
                                onClick={() => { setErr(''); setStep(1); }}>
                                <IconArrowLeft width={16} height={16}/> Назад
                            </button>
                            <button type="submit" className={cl.btnPrimary} disabled={loading}>
                                {loading
                                    ? 'Регистрируем…'
                                    : (<>Создать центр <IconCheck width={16} height={16} strokeWidth={3}/></>)
                                }
                            </button>
                        </div>
                    </>
                )}

                <div className={cl.altLink}>
                    Уже есть аккаунт? <Link to="/login">Войти</Link>
                </div>
            </form>
            {showGoogleHelp && (
                <GoogleMigrationModal onClose={() => setShowGoogleHelp(false)} />
            )}
        </main>
    );
}


function ConsentCheckbox({ id, checked, onChange, children }) {
    return (
        <label className={cl.consentRow} htmlFor={id}>
            <input
                type="checkbox"
                id={id}
                checked={checked}
                onChange={e => onChange(e.target.checked)}
                required
            />
            <span className={cl.consentText}>{children}</span>
        </label>
    );
}
